import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper } from 'src/api/utils/errorHandlers';
import { retryOnTransient } from 'src/api/utils/helpers';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import { DestinationsResponse } from 'src/types/TAnalyticsDestinations';
import { getDestinations } from '../functions/analyticsDestinationsApi';
import { queryNames } from '../utils/queryNames';

export function useDestinationsQuery(
  queryOptions?: UseQueryOptions<DestinationsResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getDestinations(accessToken)),
    [accessToken],
  );
  return useQuery<DestinationsResponse, FetchError>(
    [queryNames.analyticsDestinations],
    queryFn,
    // `retry: retryOnTransient` matters here beyond matching its siblings.
    // This endpoint is plan-gated (ANALYTICS_INTEGRATIONS, Team+), so for every
    // account below it the answer is an authoritative 403 `plan_upgrade_required`
    // that no amount of retrying can change. Left on react-query's default of
    // three retries with exponential backoff, the page sat on a spinner for
    // ~8s (1s + 2s + 4s) before showing the upgrade prompt it already had —
    // measured on prod, on a DEMO-plan trial, arriving from the "Set up
    // Analytics Integrations" link on Delivery Insights.
    { staleTime: 30_000, retry: retryOnTransient, ...queryOptions },
  );
}
